import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Flex, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type NextBestAction } from '@post-office/confluence-next-best-action';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import type { ClickedElementAttributes } from '@post-office/universal-contracts/message-instance-event';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useEffect } from 'react';

import { CREATE_WITH_ROVO_IMAGE } from '../../assets/create-with-rovo';
import { type JSX } from 'react/jsx-runtime';

const i18n = defineMessages({
	headingLabel: {
		id: 'post-office.confluence-create-with-rovo-side-nav-nudge.heading',
		defaultMessage: 'You describe, Rovo creates',
		description: 'Heading for the Create with Rovo side nav nudge.',
	},
	bodyLabel: {
		id: 'post-office.confluence-create-with-rovo-side-nav-nudge.body',
		defaultMessage: 'With a simple prompt, Rovo can draft a page or whiteboard.',
		description: 'Body text for the Create with Rovo side nav nudge.',
	},
	callToActionLabel: {
		id: 'post-office.confluence-create-with-rovo-side-nav-nudge.call.to.action',
		defaultMessage: 'Show me how',
		description: 'Call to action button on the Create with Rovo side nav nudge.',
	},
	dismissLabel: {
		id: 'post-office.confluence-create-with-rovo-side-nav-nudge.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Dismiss button on the Create with Rovo side nav nudge.',
	},
});

const styles = cssMap({
	root: {
		position: 'relative',

		flexGrow: 1,

		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
	},

	primaryContentContainer: {
		flexGrow: 1,

		padding: token('space.200'),
	},

	callToActionContainer: {
		cursor: 'pointer',
		padding: '0',
		backgroundColor: 'transparent',

		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},

	imageContainer: {
		flex: '0 0 auto',
	},

	closeButtonContainer: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
	},
});

type CreateWithRovoMessageInstanceClickedParams = {
	actionType: 'open' | 'dismiss';
	clickedElement: ClickedElementAttributes;
};

export type CreateWithRovoProps = {
	action: NextBestAction;
	onPrimaryActionClick?: (nextBestActionType: NextBestAction) => string | void | null;
	onDismissClick?: () => void;
};

export const CreateWithRovo = ({
	action,
	onPrimaryActionClick,
	onDismissClick,
}: CreateWithRovoProps): JSX.Element => {
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();

	useEffect(() => {
		if (!messageInstanceViewed) {
			return;
		}

		void messageInstanceViewed();
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'ConfluenceCreateWithRovoSideNavNudge',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fireMessageInstanceClicked = ({
		actionType,
		clickedElement,
	}: CreateWithRovoMessageInstanceClickedParams) => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: clickedElement,
			action: {
				actionType: actionType,
			},
			additionalMetadataFromFrontend: {},
		});
	};

	const handleOnPrimaryActionClick = () => {
		void fireMessageInstanceClicked({
			actionType: 'open',
			clickedElement: {
				clickIdentifier: 'showMeHow',
				clickedElement: 'button',
				buttonType: 'primary',
			},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'createWithRovoSideNavNudgeCta',
		});

		void onPrimaryActionClick?.(action);
	};

	const handleOnDismissClick = () => {
		void fireMessageInstanceClicked({
			actionType: 'dismiss',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'cross-icon',
			},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'createWithRovoSideNavNudgeDismiss',
		});

		void onDismissClick?.();
	};

	return (
		<AnalyticsContext data={{ source: 'confluence-create-with-rovo-side-nav-nudge' }}>
			<Flex xcss={styles.root} testId="confluence-create-with-rovo-side-nav-nudge">
				<Flex xcss={styles.primaryContentContainer} gap="space.100" direction="column">
					<Heading size="small">
						<FormattedMessage {...i18n.headingLabel} />
					</Heading>
					<Text size="small" color="color.text.subtle">
						<FormattedMessage {...i18n.bodyLabel} />
					</Text>
					<Box>
						<Pressable xcss={styles.callToActionContainer} onClick={handleOnPrimaryActionClick}>
							<Text color="color.link" size="medium">
								<FormattedMessage {...i18n.callToActionLabel} />
							</Text>
						</Pressable>
					</Box>
				</Flex>
				<Flex xcss={styles.imageContainer} alignItems="center">
					<CREATE_WITH_ROVO_IMAGE />
				</Flex>
				<Box xcss={styles.closeButtonContainer}>
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={(props) => <CrossIcon {...props} size="small" />}
						label={<FormattedMessage {...i18n.dismissLabel} />}
						isTooltipDisabled={false}
						onClick={handleOnDismissClick}
					/>
				</Box>
			</Flex>
		</AnalyticsContext>
	);
};
